import { safeFetch } from "../utils/safeFetch";

async function parseJsonError(response, fallback) {
  try {
    const error = await response.json();
    const message = typeof error?.error === "string"
      ? error.error
      : typeof error?.message === "string"
        ? error.message
        : fallback;
    const err = new Error(message);
    err.code = error?.code;
    err.details = error?.details;
    err.status = response.status;
    throw err;
  } catch (err) {
    if (err?.status) throw err;
    throw new Error(fallback);
  }
}

export async function createReservationEvent(payload, token, tenant) {
  const response = await safeFetch(`/api/reservation-events`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await parseJsonError(response, `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function createReservationEventPrepayment(eventId, payload, token, tenant) {
  const response = await safeFetch(`/api/reservation-events/${eventId}/prepayments`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await parseJsonError(response, `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function getReservationEventPrepaymentSummary(eventId, token, tenant) {
  const response = await safeFetch(`/api/reservation-events/${eventId}/prepayments/summary`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
  });

  if (!response.ok) {
    await parseJsonError(response, `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function refundReservationEventPrepayment(eventId, movementId, payload, token, tenant) {
  const response = await safeFetch(`/api/reservation-events/${eventId}/prepayments/${movementId}/refund`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await parseJsonError(response, `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function reverseReservationEventPrepayment(eventId, movementId, payload, token, tenant) {
  const response = await safeFetch(`/api/reservation-events/${eventId}/prepayments/${movementId}/reverse`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await parseJsonError(response, `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function applyReservationEventPrepayments(eventId, payload, token, tenant) {
  const response = await safeFetch(`/api/reservation-events/${eventId}/prepayments/apply`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await parseJsonError(response, `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function getReservationEvents(token, tenant, query = {}) {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.eventName) params.set("eventName", query.eventName);

  const response = await safeFetch(`/api/reservation-events?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
  });

  if (!response.ok) {
    await parseJsonError(response, `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function getReservationEventsDailyBoard(token, tenant, query = {}) {
  const params = new URLSearchParams();
  if (query.roomId) params.set("roomId", query.roomId);

  const response = await safeFetch(`/api/reservation-events/daily-board?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
  });

  if (!response.ok) {
    await parseJsonError(response, `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function patchReservationEventStatus(eventId, status, token, tenant, cancellationReason) {
  const response = await safeFetch(`/api/reservation-events/${eventId}/status`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify({
      status,
      ...(cancellationReason ? { cancellationReason } : {}),
    }),
  });

  if (!response.ok) {
    await parseJsonError(response, `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function updateReservationEvent(eventId, payload, token, tenant) {
  const response = await safeFetch(`/api/reservation-events/${eventId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await parseJsonError(response, `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function markReservationEventQuoteSent(eventId, token, tenant) {
  const response = await safeFetch(`/api/reservation-events/${eventId}/quote-sent`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function uploadReservationEventAttachment(eventId, file, description, token, tenant) {
  const fd = new FormData();
  fd.append("file", file);
  if (description) fd.append("description", description);

  const response = await safeFetch(`/api/reservation-events/${eventId}/attachments`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: fd,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

function buildSummaryParams(filters = {}) {
  const params = new URLSearchParams();
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.status) params.set("status", filters.status);
  if (filters.tableId) params.set("tableId", filters.tableId);
  if (filters.tableName) params.set("tableName", filters.tableName);
  if (filters.q) params.set("q", filters.q);
  if (Array.isArray(filters.categoryIds) && filters.categoryIds.length > 0) {
    params.set("categoryIds", filters.categoryIds.join(","));
  }
  if (filters.categoryNames) params.set("categoryNames", filters.categoryNames);
  if (filters.hidePrices) params.set("hidePrices", "true");
  return params;
}

export async function downloadReservationEventSummaryPdf(filters, token, tenant) {
  const params = buildSummaryParams(filters);
  const response = await safeFetch(`/api/reservation-events/summary/pdf?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }
  return response.blob();
}

export async function downloadReservationEventProformaPdf(eventId, filters, token, tenant) {
  const params = buildSummaryParams(filters);
  if (Array.isArray(filters?.dayIds) && filters.dayIds.length > 0) params.set("dayIds", filters.dayIds.join(","));
  if (Array.isArray(filters?.packageIds) && filters.packageIds.length > 0) params.set("packageIds", filters.packageIds.join(","));
  if (Array.isArray(filters?.serviceIds) && filters.serviceIds.length > 0) params.set("serviceIds", filters.serviceIds.join(","));
  if (Array.isArray(filters?.customRowIds) && filters.customRowIds.length > 0) params.set("customRowIds", filters.customRowIds.join(","));

  const response = await safeFetch(`/api/reservation-events/${eventId}/proforma/pdf?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }
  return response.blob();
}

export async function sendReservationEventSummaryEmail(payload, token, tenant) {
  const response = await safeFetch(`/api/reservation-events/summary/email`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function deleteReservationEvent(eventId, token, tenant) {
  const response = await safeFetch(`/api/reservation-events/${eventId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }
}
